import type { AircraftSpec, MxInspection } from "./types";

/**
 * Structured facts only — do not paste OEM manuals here.
 *
 * Authority: (1) Emerson desk book, (2) Raytheon Spec & Description Dec 2004
 * S/N 258717+ (`raytheon-spec-2004`), (3) Jetcraft public overview
 * (`jetcraft-800xp`). Prefer Spec when those two disagree on OEM numbers.
 * Emerson always wins for desk-truth weights / perf already on the line.
 *
 * Variant: FlightFX **Hawker 800XP (no winglets)** baseline — not 850XP,
 * not Aviation Partners XP2 Scimitar-tip blended winglets, not XPR.
 * Do not apply a winglet range bump or ~7% burn cut.
 */
export const HAWKER_SOURCE = {
  emerson: "emerson-desk",
  raytheonSpec2004: "raytheon-spec-2004",
  jetcraft: "jetcraft-800xp",
} as const;

export const HAWKER_800XP_SOURCES = {
  "emerson-desk": {
    cite: "emerson-desk" as const,
    title: "Emerson / FlightFX 800XP desk book (Celtic Air)",
    url: "",
    note: "Primary desk truth for weights, payload, TOD/LDG, cruise, NBAA 2470, ceiling, and AMS.",
  },
  "raytheon-spec-2004": {
    cite: "raytheon-spec-2004" as const,
    title: "Raytheon Aircraft, Hawker 800XP Aircraft Specification & Description, December 2004 (S/N 258717, 258723 and on)",
    url: "https://www.adoair.aero/assets/docs/sertifika/4-Hawker800XP_Spec_Description.pdf",
    note: "Public OEM evaluation spec. Structured facts only — not a copy of the PDF.",
  },
  "jetcraft-800xp": {
    cite: "jetcraft-800xp" as const,
    title: "Jetcraft, Hawker 800XP Overview (1995–2005)",
    url: "https://www.jetcraft.com/jetstream/2020/08/hawker-800xp-overview-1995-2005-2/",
    note: "Public broker fact sheet. Prefer Spec when OEM numbers disagree.",
  },
} as const;

/**
 * Emerson-supplied FlightFX Hawker 800XP (H25B) book — **no winglets**.
 * OEM extras are Raytheon Spec baseline (non-winglet). Used by the seeded
 * tail, RFQ realism, trip planning, and quotes.
 */
export const HAWKER_800XP = {
  icaoType: "H25B",
  name: "Hawker 800XP",
  /** Desk / SOP label. FlightFX sim is the non-winglet 800XP. */
  variantLabel: "Hawker 800XP (no winglets)",
  variant: "no-winglets",
  winglets: false,
  /** Do not apply XP2 / 850 / XPR range (~+7%) or burn-cut modifiers. */
  wingletRangeBonusPct: 0,
  wingletBurnCutPct: 0,
  excludedVariants: ["850XP", "XP2", "XPR"] as const,
  mtowLb: 28000,
  mlwLb: 23350,
  mzfwLb: 18450,
  /**
   * Basic operating weight / BOW (typical). Emerson Weights & Capacities
   * table: 16010 lb. Jetcraft listed 16245 — Emerson wins.
   */
  bowLb: 16010,
  maxFuelLb: 10000,
  /** Ventral tank is part of the 10000 lb usable — not extra fuel. */
  ventralFuelLb: 1500,
  /** MZFW − BOW = 18450 − 16010. Emerson table: 2440 lb. */
  maxPayloadLb: 2440,
  /** Emerson table planning figure — not derived from MTOW − BOW − full fuel. */
  payloadFullFuelLb: 1750,
  /** Takeoff distance SL ISA (ft). */
  takeoffDistanceFt: 5032,
  /** Landing distance, 4 pax (ft) — Emerson / Jetcraft. Primary desk number. */
  landingDistanceFt: 2245,
  maxCruiseKt: 447,
  longRangeCruiseKt: 402,
  /** NBAA IFR max range (nm) — Emerson desk primary. */
  rangeNm: 2470,
  rangeNmNbaaIfr: 2470,
  ceilingFt: 41000,
  typicalHoursPerYear: 400,
  hourlyCheckHours: 400,
  /**
   * CAR 704 AMS is manufacturer Chapter 5. There is no mandatory 50-hour
   * inspection on the 800XP. Do not seed or require one.
   */
  mandatory50HourCheck: false,
  eCheckMonths: 12,
  fCheckMonths: 24,
  gCheckMonths: 48,
  gearOverhaulMonths: 144,

  /** Max ramp — Spec / Jetcraft. */
  mrwLb: 28120,
  engineCount: 2,
  engineMake: "Honeywell",
  engineModel: "TFE731-5BR",
  thrustLbfEach: 4660,
  thrustReversers: "target-type hydraulic",
  apuModel: "Honeywell 36-150[W]",
  apuInFlightCeilingFt: 30000,
  apuElectrical: "28V/300A class",
  fuelDensityLbPerUsGal: 6.7,
  approvedFuels: ["Jet A", "Jet A-1", "JP-4", "JP-8"] as const,
  lengthFt: 51,
  lengthIn: 2,
  spanFt: 51,
  spanIn: 4,
  heightBowFt: 18,
  heightBowIn: 1,
  wingAreaSqFt: 374,
  cabinLengthFt: 21,
  cabinLengthIn: 4,
  cabinHeightFt: 5,
  cabinHeightIn: 9,
  cabinWidthFt: 6,
  cabinWidthIn: 0,
  cabinFloorWidthFt: 4,
  cabinFloorWidthIn: 3,
  /** Spec cabin volume (cu ft). Prefer for OEM. */
  cabinVolumeCuFtOemSpec: 604,
  /** Jetcraft quotes 551 cu ft. */
  cabinVolumeCuFtJetcraft: 551,
  typicalPax: 8,
  typicalCrew: 2,
  baggageInternalCuFt: 33,
  baggageAftCuFt: 16.5,
  pressurizationDeltaPsi: 8.55,
  cabinAltAtCeilingFt: 7500,
  mmo: 0.8,
  vmoVentralFullKias: 280,
  flapPositionsDeg: [0, 15, 25, 45] as const,
  vfeKias: { flap15: 220, flap25: 175, flap45: 165 } as const,
  vloKias: 220,
  vleKias: 220,
  noiseTakeoffEpndb: 79.3,
  noiseApproachEpndb: 93.3,
  noiseLateralEpndb: 87.1,
  /** Spec NBAA IFR ~2540 nm (4 pax + 2 crew, ±3%). Alternate — desk uses 2470. */
  rangeNmOemSpec2540: 2540,
  /** Spec FAR 25 landing at MLW. Alternate — desk uses 2245 (4 pax). */
  landingDistanceFtOemSpecMlw: 2650,
  /**
   * Combined cruise-class USGPH. Jetcraft ballpark — not AFM / not OEM guaranteed.
   */
  fuelBurnUsgphTypical: 217,
  fuelBurnUsgphBrokerLow: 250,
  fuelBurnUsgphBrokerHigh: 291,
  productionStartYear: 1995,
  productionEndYear: 2005,
  deliveriesApprox: 426,
  iceProtection: "TKS wings/tailplane; engine inlet bleed; heated windshields/pitots",
  avionicsClass: "Collins Pro Line 21 era",
} as const;

export const HAWKER_800XP_FERRY_RANGE_NM = Math.round(HAWKER_800XP.rangeNm * 1.1);

/**
 * Practical charter / ferry stage limit as a fraction of published range.
 * Already used by `validForAircraft` — do not invent a second jet-physics number.
 */
export const CHARTER_RANGE_FACTOR = 0.92;

/** NBAA-style practical charter range (Hawker 2470 × 0.92 ≈ 2272 NM). */
export function practicalCharterRangeNm(
  spec: Pick<AircraftSpec, "rangeNm"> & Partial<Pick<AircraftSpec, "ferryRangeNm">>,
  ferry = false,
): number {
  const published = ferry ? (spec.ferryRangeNm ?? spec.rangeNm) : spec.rangeNm;
  return published * CHARTER_RANGE_FACTOR;
}

/** Direct city-pair exceeds the assigned type’s practical range. */
export function needsTechStop(
  distanceNm: number,
  spec: Pick<AircraftSpec, "rangeNm"> & Partial<Pick<AircraftSpec, "ferryRangeNm">>,
  ferry = false,
): boolean {
  if (!Number.isFinite(distanceNm) || distanceNm <= 0) return false;
  return distanceNm > practicalCharterRangeNm(spec, ferry);
}

/**
 * SL ISA takeoff is 5032 ft; CYPD is 5000. Charter weights are below MTOW,
 * so 50 ft of operational slop keeps the home field legal without inventing a
 * shorter published number.
 */
export const RUNWAY_OPERATIONAL_SLOP_FT = 50;

/** Below this, show a PIC warning. Never a hard assign/schedule reject. */
export const PIC_RUNWAY_WARN_FT = 5000;

export function runwayPicWarning(runwayFt: number | undefined | null): string | null {
  const ft = Number(runwayFt);
  if (!Number.isFinite(ft) || ft <= 0) return null;
  if (ft < PIC_RUNWAY_WARN_FT) {
    return `${Math.round(ft)} ft is under ${PIC_RUNWAY_WARN_FT} ft — PIC decides.`;
  }
  return null;
}

export function runwayPicPairWarning(
  origin: { icao: string; runwayFt?: number },
  dest: { icao: string; runwayFt?: number },
): string | null {
  const bits = [
    runwayPicWarning(origin.runwayFt) ? `${origin.icao} ${runwayPicWarning(origin.runwayFt)}` : null,
    runwayPicWarning(dest.runwayFt) ? `${dest.icao} ${runwayPicWarning(dest.runwayFt)}` : null,
  ].filter(Boolean);
  return bits.length ? bits.join(" ") : null;
}

/**
 * Shortest Hawker charter sector. Home-region hops (about 120 NM and up) count.
 * Named Atlantic shorts (CYPD–CYHZ and the rest of that list) stay off the jet.
 */
export const HAWKER_MIN_STAGE_NM = 120;

export function hawkerEngineLabel(): string {
  return `${HAWKER_800XP.engineCount} × ${HAWKER_800XP.engineMake} ${HAWKER_800XP.engineModel}`;
}

export function hawkerCabinLabel(): string {
  return `${HAWKER_800XP.typicalPax} pax + ${HAWKER_800XP.typicalCrew} crew`;
}

/** One SOP / RFQ line — not a wall of text. */
export function hawkerSopTypeLine(): string {
  return `${HAWKER_800XP.variantLabel}: ${hawkerEngineLabel()} (${HAWKER_800XP.thrustLbfEach} lbf), ${hawkerCabinLabel()}, ramp ${HAWKER_800XP.mrwLb} / MTOW ${HAWKER_800XP.mtowLb} lb. Planning burn ~${HAWKER_800XP.fuelBurnUsgphTypical} USGPH (broker ballpark, not AFM). Not 850XP / XP2 / XPR. ${HAWKER_800XP.iceProtection}. ${HAWKER_800XP.avionicsClass}.`;
}

export function hawkerApprovedFuelsHint(): string {
  return `Hawker OEM planning density ${HAWKER_800XP.fuelDensityLbPerUsGal} lb/US gal; approved ${HAWKER_800XP.approvedFuels.join(" / ")} (catalog hint, cold-weather notes in the AFM).`;
}

/** Planning burn for quotes — Jetcraft typical, not AFM truth. */
export function hawkerPlanningBurnGph(): number {
  return HAWKER_800XP.fuelBurnUsgphTypical;
}

export function monthsToHours(
  months: number,
  hoursPerYear: number = HAWKER_800XP.typicalHoursPerYear,
): number {
  return Math.round((months * hoursPerYear) / 12);
}

export function lastBlockHours(hours: number, interval: number): number {
  if (interval <= 0) return 0;
  return hours - (hours % interval);
}

export function nextBlockDueHours(hours: number, interval: number): number {
  if (interval <= 0) return hours;
  const rem = hours % interval;
  return rem === 0 ? hours + interval : hours + (interval - rem);
}

export function seedHawkerInspections(airframeHours: number): MxInspection[] {
  const hourly = HAWKER_800XP.hourlyCheckHours;
  const calendar: Array<{ id: string; label: string; months: number }> = [
    { id: "insp-e-check", label: "E check", months: HAWKER_800XP.eCheckMonths },
    { id: "insp-f-check", label: "F check", months: HAWKER_800XP.fCheckMonths },
    { id: "insp-g-check", label: "G check", months: HAWKER_800XP.gCheckMonths },
    { id: "insp-gear-oh", label: "Landing gear overhaul", months: HAWKER_800XP.gearOverhaulMonths },
  ];
  const hourlyItem: MxInspection = {
    id: "insp-bcd-400",
    label: "B / C / D check (400 h)",
    intervalHours: hourly,
    dueHours: nextBlockDueHours(airframeHours, hourly),
    lastCompletedHours: lastBlockHours(airframeHours, hourly),
  };
  return [
    hourlyItem,
    ...calendar.map((row) => {
      const intervalHours = monthsToHours(row.months);
      return {
        id: row.id,
        label: row.label,
        intervalHours,
        intervalMonths: row.months,
        dueHours: nextBlockDueHours(airframeHours, intervalHours),
        lastCompletedHours: lastBlockHours(airframeHours, intervalHours),
      };
    }),
  ];
}

export function isHawker800xpType(icaoType: string | undefined): boolean {
  return (icaoType ?? "").toUpperCase() === HAWKER_800XP.icaoType;
}

/** Planning TAS: max cruise, or LRC on long sectors (≥ 55% of NBAA range). */
export function planningCruiseKt(spec: Pick<AircraftSpec, "cruiseKt" | "longRangeCruiseKt" | "rangeNm">, distanceNm?: number): number {
  const lrc = spec.longRangeCruiseKt;
  if (
    lrc &&
    distanceNm != null &&
    spec.rangeNm > 0 &&
    distanceNm >= spec.rangeNm * 0.55
  ) {
    return lrc;
  }
  return spec.cruiseKt;
}

export function runwayRequiredFt(spec: Pick<AircraftSpec, "minRunwayFt" | "takeoffDistanceFt">): number {
  return spec.takeoffDistanceFt ?? spec.minRunwayFt;
}

/**
 * Book-length check only. Do not hard-block assign/schedule on this —
 * PIC decides. Prefer `runwayPicWarning` for under-5000 ft caution.
 */
export function runwayFitsAircraft(
  spec: Pick<AircraftSpec, "minRunwayFt" | "takeoffDistanceFt">,
  destRunwayFt: number,
): boolean {
  if (destRunwayFt <= 0) return true;
  return destRunwayFt + RUNWAY_OPERATIONAL_SLOP_FT >= runwayRequiredFt(spec);
}

export function cargoCapFromPayload(spec: Pick<AircraftSpec, "maxPax" | "maxPayloadLb">, paxCount: number): number {
  const paxLb = Math.max(0, paxCount) * 200;
  if (spec.maxPayloadLb != null) return Math.max(0, spec.maxPayloadLb - paxLb);
  return Math.max(180, (spec.maxPax - paxCount) * 220 + 250);
}

/** Next 400 h block after the seeded airframe time (4120 → 4400). */
export const HAWKER_800XP_SEED_HOURS = 4120;
export const HAWKER_800XP_SEED_NEXT_INSPECTION = nextBlockDueHours(
  HAWKER_800XP_SEED_HOURS,
  HAWKER_800XP.hourlyCheckHours,
);
